// Customer Intelligence data layer (Phase 4, P4-CC9) — READ-ONLY aggregation.
//
// Deterministic, explainable RFM + payment-behaviour scoring over existing data —
// no tables, no writes, no paid AI. Reuses invoice_ledger (outstanding/overdue),
// invoices (recency/frequency/value) and invoice_payments (payment behaviour).
// Company-scoping is automatic (reads run as the logged-in user via RLS).
//
// MODEL (approved):
//   R (1–5): days_since_last_order  ≤30→5 ≤90→4 ≤180→3 ≤365→2 else/never→1
//   F (1–5): order_count            ≥12→5 ≥6→4 ≥3→3 ≥1→2 0→1
//   M (1–5): lifetime_value quintile across the active base (top 20%→5 … →1);
//            rank-scaled when <5 customers; 0-value → 1
//   P: on_time_ratio×100 (neutral 50 when no payment history)
//   score = round(0.30·R̂ + 0.25·F̂ + 0.30·M̂ + 0.15·P),  X̂ = (X/5)·100
//   segments: recent = days_since_last_order ≤ 120; wasValuable = (M≥4 or F≥4)
//     order_count 0 → Inactive · recent & ≥80 → Champion · recent & 60–79 & F≥3 → Loyal
//     recent else → Potential · !recent & wasValuable → At Risk · !recent else → Inactive

import { supabase } from "./supabaseClient";
import { listCustomers } from "./customers";   // read-only import
import { todayISO, daysBetweenISO } from "./dates";

const LEDGER = "invoice_ledger";
const PAYMENTS = "invoice_payments";
const MAX_ROWS = 9999;
const ISSUED = ["sent", "partial", "paid", "credited"];

export const SEGMENTS = ["Champion", "Loyal", "Potential", "At Risk", "Inactive"];
export const CURRENCY = "EUR";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const round2n = (n) => (n == null ? null : Math.round(Number(n) * 100) / 100);

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "42501": message = "You do not have access to this data."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

const recencyScore = (days) => (days == null ? 1 : days <= 30 ? 5 : days <= 90 ? 4 : days <= 180 ? 3 : days <= 365 ? 2 : 1);
const frequencyScore = (n) => (n >= 12 ? 5 : n >= 6 ? 4 : n >= 3 ? 3 : n >= 1 ? 2 : 1);

/** Monetary 1–5 across the base: quintiles for n≥5, rank-scaled for n<5, 0-value → 1. */
function monetaryMap(custList) {
  const sorted = [...custList].sort((a, b) => a._ltv - b._ltv);
  const n = sorted.length;
  const map = new Map();
  sorted.forEach((c, i) => {
    let M;
    if (c._ltv <= 0) M = 1;
    else if (n === 1) M = 5;
    else if (n < 5) M = Math.round(1 + 4 * (i / (n - 1)));           // rank-scale
    else M = Math.min(5, Math.floor((i / n) * 5) + 1);              // quintile (ascending → top 20% = 5)
    map.set(c.id, M);
  });
  return map;
}

function segmentOf(order_count, days_since_last_order, score, F, M) {
  if (order_count === 0) return "Inactive";
  const recent = days_since_last_order != null && days_since_last_order <= 120;
  const wasValuable = M >= 4 || F >= 4;
  if (recent) {
    if (score >= 80) return "Champion";
    if (score >= 60 && F >= 3) return "Loyal";
    return "Potential";
  }
  return wasValuable ? "At Risk" : "Inactive";
}

/**
 * Score + segment every active customer from their invoice / payment history.
 * @returns {Promise<{data:Array, summary:Object, count:number}>}
 */
export async function getCustomerScores() {
  const c = client();
  const [customers, ledgerRes, payRes] = await Promise.all([
    listCustomers({ status: "active" }),
    c.from(LEDGER).select("id, customer_id, total_amount, amount_outstanding, issue_date, due_date").in("status", ISSUED).range(0, MAX_ROWS),
    c.from(PAYMENTS).select("invoice_id, amount, payment_date").eq("voided", false).range(0, MAX_ROWS),
  ]);
  if (ledgerRes.error) throw toError(ledgerRes.error, "Could not load invoices");
  if (payRes.error) throw toError(payRes.error, "Could not load payments");
  const invoices = ledgerRes.data || [];
  const payments = payRes.data || [];
  const t0 = todayISO();
  const invById = new Map(invoices.map((i) => [i.id, i]));   // invoice_id → { customer_id, issue_date, due_date }

  const agg = new Map();
  const ensure = (cid) => {
    if (!agg.has(cid)) agg.set(cid, { ltv: 0, orders: 0, lastOrder: null, outstanding: 0, overdue: 0, paidTotal: 0, dtpSum: 0, dtpCount: 0, onTime: 0, dueCount: 0 });
    return agg.get(cid);
  };

  for (const i of invoices) {
    if (!i.customer_id) continue;
    const a = ensure(i.customer_id);
    a.ltv += Number(i.total_amount) || 0;
    a.orders += 1;
    if (i.issue_date && (!a.lastOrder || i.issue_date > a.lastOrder)) a.lastOrder = i.issue_date;
    const out = Number(i.amount_outstanding) || 0;
    a.outstanding += out;
    if (out > 0 && i.due_date && i.due_date < t0) a.overdue += out;
  }
  for (const p of payments) {
    const inv = invById.get(p.invoice_id);
    if (!inv || !inv.customer_id) continue;   // payment on a non-issued/foreign invoice → skip
    const a = ensure(inv.customer_id);
    a.paidTotal += Number(p.amount) || 0;
    // daysBetweenISO(a, b) returns b - a (src/lib/dates.js) — so days-to-pay is
    // (payment_date - issue_date), which means issue_date goes FIRST. The
    // arguments were reversed here until P4-CC19b-0, yielding issue - payment:
    // negative for every normally-paid invoice, so avg_days_to_pay rendered as a
    // negative number in the Intelligence table. Same direction as the correct
    // usages in statements.js and invoices.js — do not swap these back.
    if (inv.issue_date && p.payment_date) { a.dtpSum += daysBetweenISO(inv.issue_date, p.payment_date); a.dtpCount += 1; }
    if (inv.due_date && p.payment_date) { a.dueCount += 1; if (p.payment_date <= inv.due_date) a.onTime += 1; }
  }

  const custList = (customers || []).map((cu) => ({ id: cu.id, _ltv: agg.get(cu.id)?.ltv || 0, cu }));
  const mMap = monetaryMap(custList);

  const data = custList.map(({ id, cu }) => {
    const a = agg.get(id) || { ltv: 0, orders: 0, lastOrder: null, outstanding: 0, overdue: 0, paidTotal: 0, dtpSum: 0, dtpCount: 0, onTime: 0, dueCount: 0 };
    // daysBetweenISO(a, b) returns b - a (src/lib/dates.js) — so "days since the
    // last order" is (today - lastOrder), which means lastOrder goes FIRST. The
    // arguments were reversed here until P4-CC19b-0, yielding lastOrder - today:
    // negative for any past order, which Math.max(0, ...) then clamped to 0. The
    // effect was total, not marginal — EVERY customer who had ever ordered showed
    // "0d ago", scored R=5, and counted as "recent", which made the "At Risk"
    // segment structurally unreachable and its KPI permanently 0.
    // Math.max(0, ...) is kept deliberately: it now guards only what it was for,
    // a future-dated order.
    const days_since_last_order = a.lastOrder ? Math.max(0, daysBetweenISO(a.lastOrder, t0)) : null;
    const R = recencyScore(days_since_last_order);
    const F = frequencyScore(a.orders);
    const M = mMap.get(id) || 1;
    const on_time_ratio = a.dueCount > 0 ? a.onTime / a.dueCount : null;
    const P100 = on_time_ratio == null ? 50 : on_time_ratio * 100;
    const score = Math.round(0.30 * (R / 5) * 100 + 0.25 * (F / 5) * 100 + 0.30 * (M / 5) * 100 + 0.15 * P100);
    const segment = segmentOf(a.orders, days_since_last_order, score, F, M);
    return {
      id, company_name: cu.company_name, customer_number: cu.customer_number,
      score, segment,
      lifetime_value: round2(a.ltv), paid_total: round2(a.paidTotal), order_count: a.orders,
      last_order_date: a.lastOrder, days_since_last_order,
      avg_days_to_pay: a.dtpCount > 0 ? Math.round(a.dtpSum / a.dtpCount) : null,
      on_time_ratio: round2n(on_time_ratio),
      overdue_amount: round2(a.overdue), outstanding: round2(a.outstanding),
      factors: { R, F, M, P: Math.round(P100), recency_days: days_since_last_order, order_count: a.orders, monetary_quintile: M, on_time_ratio: round2n(on_time_ratio) },
    };
  });

  return { data, summary: getIntelligenceSummary(data), count: data.length };
}

/**
 * Summarise a scored array into KPIs. Pure (no fetch) — reused inline as `summary`.
 * @returns {{champions:number, atRisk:number, revenueFromTop20:number, avgScore:number, totalCustomers:number, bySegment:Object}}
 */
export function getIntelligenceSummary(scores) {
  const rows = scores || [];
  const bySegment = { Champion: 0, Loyal: 0, Potential: 0, "At Risk": 0, Inactive: 0 };
  let scoreSum = 0;
  for (const r of rows) { if (bySegment[r.segment] != null) bySegment[r.segment] += 1; scoreSum += r.score || 0; }
  const sortedByLtv = [...rows].sort((a, b) => (b.lifetime_value || 0) - (a.lifetime_value || 0));
  const topN = Math.max(1, Math.ceil(rows.length * 0.2));
  const revenueFromTop20 = round2(sortedByLtv.slice(0, topN).reduce((s, r) => s + (r.lifetime_value || 0), 0));
  return {
    champions: bySegment.Champion,
    atRisk: bySegment["At Risk"],
    revenueFromTop20,
    avgScore: rows.length ? Math.round(scoreSum / rows.length) : 0,
    totalCustomers: rows.length,
    bySegment,
  };
}
